import {
  EnabledStatus as WarehouseEnabledStatus,
  overseasWarehouseApi,
  type OverseasWarehouseVO
} from '@/api/basic-data/overseas-warehouse.service'

const WAREHOUSE_PAGE_SIZE = 200

type UnknownRecord = Record<string, unknown>
type WarehouseValueField = 'name' | 'code'

export interface DestinationWarehouseSelectOption {
  label: string
  value: string
  code: string
  content: OverseasWarehouseVO
}

export interface DestinationWarehouseFields {
  destinationWh?: string | null
  destinationWhCode?: string | null
}

export interface ConsigneeAddressTarget {
  company?: string | null
  contactName?: string | null
  phone?: string | null
  country?: string | number | null
  province?: string | number | null
  city?: string | number | null
  citySource?: 1 | 2 | null
  district?: string | number | null
  town?: string | null
  address?: string | null
  postcode?: string | null
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function getRecordContent(value: unknown): UnknownRecord {
  const record = isRecord(value) ? value : {}
  return isRecord(record.content) ? record.content : record
}

function getStringProp(record: UnknownRecord, key: string): string {
  const value = record[key]
  return value === undefined || value === null ? '' : String(value).trim()
}

function unwrapRecords(response: unknown): OverseasWarehouseVO[] {
  const payload = isRecord(response) && 'data' in response ? response.data : response
  if (Array.isArray(payload)) return payload as OverseasWarehouseVO[]
  if (isRecord(payload) && Array.isArray(payload.records)) {
    return payload.records as OverseasWarehouseVO[]
  }
  return []
}

function getWarehouseName(value: unknown): string {
  const content = getRecordContent(value)
  const record = isRecord(value) ? value : {}
  return (
    getStringProp(content, 'warehouseNameCn') ||
    getStringProp(content, 'warehouseNameEn') ||
    getStringProp(record, 'nameCn') ||
    getStringProp(record, 'nameEn') ||
    getStringProp(record, 'label')
  )
}

function getWarehouseCode(value: unknown): string {
  const content = getRecordContent(value)
  const record = isRecord(value) ? value : {}
  return (
    getStringProp(content, 'warehouseCode') ||
    getStringProp(content, 'storageCode') ||
    getStringProp(content, 'storage_code') ||
    getStringProp(record, 'code')
  )
}

function formatWarehouse(value: unknown, codeFirst: boolean): string {
  const name = getWarehouseName(value)
  const code = getWarehouseCode(value)
  if (!name) return code
  if (!code || code === name) return name
  return codeFirst ? `${code} ${name}` : `${name} ${code}`
}

export const warehouseNameDisplayFormat = {
  formatter: (item: unknown) => formatWarehouse(item, false)
}

export const destinationWarehouseNameDisplayFormat = warehouseNameDisplayFormat

export const destinationWarehouseCodeDisplayFormat = {
  formatter: (item: unknown) => formatWarehouse(item, true)
}

function toWarehouseOption(
  item: OverseasWarehouseVO,
  valueField: WarehouseValueField
): DestinationWarehouseSelectOption {
  const name = item.warehouseNameCn || item.warehouseNameEn || item.warehouseCode
  const code = item.warehouseCode || ''
  return {
    label: code && code !== name ? `${name} ${code}` : name,
    value: valueField === 'code' ? code : name,
    code,
    content: item
  }
}

async function searchDestinationWarehouses(
  keyword: string,
  valueField: WarehouseValueField
): Promise<DestinationWarehouseSelectOption[]> {
  const response = await overseasWarehouseApi.queryPage({
    pageNum: 1,
    pageSize: WAREHOUSE_PAGE_SIZE,
    keyword: keyword?.trim() || undefined,
    enabled: WarehouseEnabledStatus.ENABLED
  })
  return unwrapRecords(response).map(item => toWarehouseOption(item, valueField))
}

export function searchWarehouseNames(keyword: string) {
  return searchDestinationWarehouses(keyword, 'name')
}

export function searchDestinationWarehouseNames(keyword: string) {
  return searchWarehouseNames(keyword)
}

export function searchDestinationWarehouseCodes(keyword: string) {
  return searchDestinationWarehouses(keyword, 'code')
}

export function resolveDestinationWarehouse(option: unknown): OverseasWarehouseVO | undefined {
  const content = getRecordContent(option)
  const code = getWarehouseCode(content)
  const name = getWarehouseName(content)
  if (!code && !name) return undefined
  return content as unknown as OverseasWarehouseVO
}

export function overwriteDestinationWarehouseFields(
  target: DestinationWarehouseFields,
  warehouse: OverseasWarehouseVO
): void {
  target.destinationWhCode = warehouse.warehouseCode || ''
  target.destinationWh = warehouse.warehouseNameCn || warehouse.warehouseNameEn || ''
}

export function clearDestinationWarehouseFields(target: DestinationWarehouseFields): void {
  target.destinationWhCode = ''
  target.destinationWh = ''
}

export function overwriteConsigneeAddress(
  target: ConsigneeAddressTarget,
  warehouse: OverseasWarehouseVO,
  countryId: string | number | null | undefined
): void {
  target.company = warehouse.warehouseNameCn || warehouse.warehouseNameEn || warehouse.warehouseCode || ''
  target.contactName = warehouse.contactPerson || ''
  target.phone = warehouse.contactPhone || ''
  target.country = countryId
  target.province = warehouse.stateProvince || null
  target.city = warehouse.city || null
  target.citySource = target.city ? 2 : null
  target.district = null
  target.town = ''
  target.address = warehouse.addressDetail || ''
  target.postcode = warehouse.postalCode || ''
}

export function clearConsigneeAddress(target: ConsigneeAddressTarget): void {
  target.company = ''
  target.contactName = ''
  target.phone = ''
  target.country = null
  target.province = null
  target.city = null
  target.citySource = null
  target.district = null
  target.town = ''
  target.address = ''
  target.postcode = ''
}
